import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { useApplicationContext } from "../context/ApplicationContext";

/**
 * TenantSelector - Displays and allows switching between different tenant environments
 *
 */
export const TenantSelector: React.FC = () => {
  const { tenantId, tenantIds, openModal } = useApplicationContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const tenantMenuOptions = useMemo(() => tenantIds || [], [tenantIds]);

  const displayTenant = sessionStorage.getItem("tenantId") || tenantId;

  return (
    <Box ref={containerRef} sx={{ display: "inline-flex" }}>
      <ButtonBase
        onClick={() => setAnchorEl(containerRef.current)}
        aria-label="Open tenant menu"
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 0.5,
          py: 0.25,
          borderRadius: 1,
          color: "#3D3C38",
        }}
      >
        <Typography
          sx={{
            fontFamily: "EnterpriseSansVF, Arial, sans-serif",
            fontWeight: 400,
            color: "#095F87",
            fontSize: "23px",
            whiteSpace: "nowrap",
          }}
        >
          {displayTenant}
        </Typography>
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            color: "#095F87",
            width: 32,
            height: 32,
            p: "4px",
            borderRadius: "32px",
            boxShadow: "0px 2px 6px 0px #3D3C3829",
            opacity: 1,
            gap: "4px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </Box>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            boxShadow: "0px 4px 16px 0px #19191A29",
            border: "1px solid #E5E5E6",
            p: 0,
            mt: "20px",
            minWidth: "275px",
          },
        }}
      >
        {tenantMenuOptions.map((tId) => (
          <MenuItem
            key={tId}
            onClick={() => {
              if (tId !== displayTenant) {
                openModal(tId);
              }
              setAnchorEl(null);
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{tId}</span>
            {tId === displayTenant && (
              <CheckIcon
                sx={{
                  fontSize: "20px",
                  color: "#3D3C38",
                  ml: 2,
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
